/**
 * The two answers a credential read can give instead of the credential itself.
 *
 * A company credential saved before signatures covered every field waits for a re-check with the
 * provider; until then it is answered 409 "being re-checked". One the provider no longer approves
 * is answered 410 "no longer verified". Pages show these as their own messages, not as an error.
 * The whole-wallet export carries the same codes per entry, without a status.
 */

export const CREDENTIAL_TRUST = Object.freeze({
  RECHECK_PENDING: 'credential_recheck_pending',
  NO_LONGER_VERIFIED: 'credential_no_longer_verified',
});

const STATUS_OF = {
  [CREDENTIAL_TRUST.RECHECK_PENDING]: 409,
  [CREDENTIAL_TRUST.NO_LONGER_VERIFIED]: 410,
};

const MESSAGE_ID_OF = {
  [CREDENTIAL_TRUST.RECHECK_PENDING]: 'CredentialTrust.recheckPending',
  [CREDENTIAL_TRUST.NO_LONGER_VERIFIED]: 'CredentialTrust.noLongerVerified',
};

/**
 * The trust state a failed read reports, or null when it reports something else.
 *
 * @param {Error|Object|string|null} failure – a request error (`status` and `error`), an export
 *   entry (`error`), or the code itself
 * @returns {string|null} one of CREDENTIAL_TRUST
 */
export const credentialTrustStateOf = failure => {
  if (!failure) return null;
  if (typeof failure === 'string') return STATUS_OF[failure] ? failure : null;
  const code = failure.error;
  if (!STATUS_OF[code]) return null;
  // A request error must also carry the status that code is sent with.
  if (failure.status !== undefined && failure.status !== STATUS_OF[code]) return null;
  return code;
};

/** The English message id for a trust state, or null for none. */
export const credentialTrustMessageId = state => MESSAGE_ID_OF[state] || null;
